import { FC, useEffect, useState } from 'react'
import styles from './EditClaimCardMainInformation.module.css'
import 'react-datepicker/dist/react-datepicker.css'
import { Formik } from 'formik'
import TitleBlockForEditPages from '../TitleBlockForEditPages/TitleBlockForEditPages'
import s from '../../styles/common/Form.module.css'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { useNavigate, useParams } from 'react-router-dom'
import PATH from '../../navigation/path'
import { ClaimResponseType } from '../../redux/actions/types'
import { changeClaimMainInformation } from '../../redux/actions/claimsAction'
import { useToast } from '../../context/ToastContext'
import CreateClientModal from '../CreateClientModal/CreateClientModal'
import CreateClientManagerModal from '../CreateClientManagerModal/CreateClientManagerModal'
import CustomModal from '../UI/CustomModal/CustomModal'
import {
  CommentTemplateType,
  createTemplate,
  deleteTemplate,
  getTemplates,
  updateTemplate,
} from '../../redux/actions/templatesAction'
import { getServices } from '../../redux/actions/servicesAction'
import { segments } from '../../constants/constants'
import { useDateAndTime } from './hooks/useDateAndTime'
import { usePlatforms } from './hooks/usePlatforms'
import { useClientSelection } from './hooks/useClientSelection'
import { prepareInitialValues } from './utils/prepareInitialValues'
import { prepareSubmitPayload } from './utils/prepareSubmitPayload'
import { PlaceInformationSection } from './sections/PlaceInformationSection'
import { ClientInformationSection } from './sections/ClientInformationSection'
import { DateTimeSection } from './sections/DateTimeSection'
import { HotelServicesSection } from './sections/HotelServicesSection'
import { EventsSection } from './sections/EventsSection'
import { StatusAndPaymentSection } from './sections/StatusAndPaymentSection'
import { FinancialInformationSection } from './sections/FinancialInformationSection'
import { AdditionalInformationSection } from './sections/AdditionalInformationSection'
import CreateCommentTemplateModal from '../CreateCommentTemplateModal/CreateCommentTemplateModal'

export type EditClaimCardMainInformationPropsType = {
  claimData: ClaimResponseType
}

const EditClaimCardMainInformation: FC<EditClaimCardMainInformationPropsType> = ({ claimData }) => {
  const [isCreateClient, setIsCreateClient] = useState(false)
  const [isCreateManager, setIsCreateManager] = useState(false)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<CommentTemplateType | null>(null)
  const [currentServiceId, setCurrentServiceId] = useState<number | null>(null)
  const [currentServiceName, setCurrentServiceName] = useState<string>('')

  const { claimId } = useParams<{ claimId: string }>()

  const { token } = useAppSelector((state) => state.auth)
  const { clients } = useAppSelector((state) => state.clients)
  const { templates: commentTemplates } = useAppSelector((state) => state.templates)
  const { services: hotelServicesFromApi } = useAppSelector((state) => state.services)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()

  const dateTimeData = useDateAndTime(claimData)
  const platformsData = usePlatforms(claimData)
  const clientSelectionData = useClientSelection(claimData, clients || [])

  const customInput = <input type='text' placeholder='Когда?' className={styles.dateValue} />

  const initialValues = prepareInitialValues(claimData)

  const handleSubmitHandler = async (values: any) => {
    const payload = prepareSubmitPayload({
      values,
      initialValues,
      selectedClient: clientSelectionData.selectedClient,
      platformId: platformsData.platformId,
      startDateString: dateTimeData.startDateString,
      finishDateString: dateTimeData.finishDateString,
      timeStart: dateTimeData.timeStart,
      timeFinish: dateTimeData.timeFinish,
      getFormatDate: dateTimeData.getFormatDate,
    })

    if (Object.keys(payload).length === 0) {
      navigate(`${PATH.claimCardPage}${claimId}`)
      return
    }

    const resultAction = await dispatch(
      changeClaimMainInformation({
        token,
        claimId: String(claimData.id),
        payload,
      }),
    )

    if (changeClaimMainInformation.rejected.match(resultAction)) {
      const errorMessage =
        (resultAction.payload as any)?.message ||
        (resultAction.payload as any)?.errors?.[0] ||
        'Произошла ошибка при изменении данных'
      showError(errorMessage)
      return
    }

    if (changeClaimMainInformation.fulfilled.match(resultAction)) {
      showSuccess('Данные успешно изменены')
      setTimeout(() => {
        navigate(`${PATH.claimCardPage}${claimId}`)
      }, 500)
    }
  }

  const handleCancel = () => {
    navigate(`${PATH.claimCardPage}${claimData.id}`)
  }

  useEffect(() => {
    if (token) {
      dispatch(getTemplates({ token }))
      dispatch(getServices({ token }))
    }
  }, [token, dispatch])

  const handleDeleteTemplate = async (templateId: number) => {
    const resultAction = await dispatch(deleteTemplate({ templateId, token }))
    if (deleteTemplate.fulfilled.match(resultAction)) {
      showSuccess('Шаблон успешно удален')
    } else {
      showError('Ошибка при удалении шаблона')
    }
  }

  const handleOpenTemplateModal = (
    serviceId: number,
    serviceName: string,
    template?: CommentTemplateType,
  ) => {
    setCurrentServiceId(serviceId)
    setCurrentServiceName(serviceName)
    setEditingTemplate(template || null)
    setIsTemplateModalOpen(true)
  }

  const handleCloseTemplateModal = () => {
    setEditingTemplate(null)
    setCurrentServiceId(null)
    setCurrentServiceName('')
    setIsTemplateModalOpen(false)
  }

  const handleTemplateSubmit = async (data: {
    name: string
    comment: string
    serviceId: number
  }) => {
    if (editingTemplate) {
      const resultAction = await dispatch(
        updateTemplate({
          templateId: editingTemplate.id,
          data,
          token,
        }),
      )
      if (updateTemplate.fulfilled.match(resultAction)) {
        showSuccess('Шаблон успешно изменен')
        handleCloseTemplateModal()
      } else {
        showError('Ошибка при обновлении шаблона')
      }
    } else {
      const resultAction = await dispatch(createTemplate({ data, token }))
      if (createTemplate.fulfilled.match(resultAction)) {
        showSuccess('Шаблон успешно создан')
        handleCloseTemplateModal()
      } else {
        showError('Ошибка при создании шаблона')
      }
    }
  }

  return (
    <div className={styles.mainInformationBlock}>
      <Formik
        key={claimData?.id || 'form'}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur
        validationSchema={''}
        onSubmit={(values) => handleSubmitHandler(values)}
      >
        {(props) => {
          const {
            values,
            touched,
            errors,
            isValid,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          } = props

          return (
            <>
              <TitleBlockForEditPages
                title='Основная информация'
                disabled={false}
                handleSubmit={handleSubmit}
                handleReset={handleCancel}
              />
              <div className={styles.container}>
                <PlaceInformationSection
                  values={values}
                  platformNames={platformsData.platformNames}
                  platformId={platformsData.platformId}
                  places={platformsData.places}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  setPlatformId={platformsData.setPlatformId}
                  setPlatformSize={platformsData.setPlatformSize}
                />

                <ClientInformationSection
                  values={values}
                  clientType={clientSelectionData.clientType}
                  selectedClient={clientSelectionData.selectedClient}
                  clients={clients || []}
                  clientOptions={clientSelectionData.clientOptions}
                  loadOptions={clientSelectionData.loadOptions}
                  setClientType={clientSelectionData.setClientType}
                  setSelectedClient={clientSelectionData.setSelectedClient}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  onOpenCreateClient={() => setIsCreateClient(!isCreateClient)}
                />

                <DateTimeSection
                  values={values}
                  dateTimeData={{
                    startDateString: dateTimeData.startDateString,
                    finishDateString: dateTimeData.finishDateString,
                    timeStart: dateTimeData.timeStart,
                    timeFinish: dateTimeData.timeFinish,
                    selectedStartDate: dateTimeData.selectedStartDate,
                    selectedFinishDate: dateTimeData.selectedFinishDate,
                    openStartDate: dateTimeData.openStartDate,
                    openFinishDate: dateTimeData.openFinishDate,
                    openStartTime: dateTimeData.openStartTime,
                    openFinishTime: dateTimeData.openFinishTime,
                    dropdownStartDataRef: dateTimeData.dropdownStartDataRef,
                    dropdownFinishDataRef: dateTimeData.dropdownFinishDataRef,
                    dropdownStartTimeRef: dateTimeData.dropdownStartTimeRef,
                    dropdownFinishTimeRef: dateTimeData.dropdownFinishTimeRef,
                    onChangeStartDate: dateTimeData.onChangeStartDate,
                    onChangeFinishDate: dateTimeData.onChangeFinishDate,
                    handleTimeStartChange: dateTimeData.handleTimeStartChange,
                    handleTimeFinishChange: dateTimeData.handleTimeFinishChange,
                    openStartDateDropDown: dateTimeData.openStartDateDropDown,
                    openFinishDateDropDown: dateTimeData.openFinishDateDropDown,
                    openStartTimeDropDown: dateTimeData.openStartTimeDropDown,
                    openFinishTimeDropDown: dateTimeData.openFinishTimeDropDown,
                  }}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  customInput={customInput}
                />

                <HotelServicesSection
                  values={values}
                  hotelServices={hotelServicesFromApi}
                  commentTemplates={commentTemplates}
                  setFieldValue={setFieldValue}
                  onOpenTemplateModal={handleOpenTemplateModal}
                  onDeleteTemplate={handleDeleteTemplate}
                />

                <EventsSection values={values} setFieldValue={setFieldValue} />

                <StatusAndPaymentSection
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />

                <FinancialInformationSection
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />

                <AdditionalInformationSection
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />

                <>
                  {touched.price && errors.price && (
                    <p className={`${s.errorValidation} ${s.networkError}`}>
                      {errors.price as any}{' '}
                    </p>
                  )}
                </>
              </div>
            </>
          )
        }}
      </Formik>
      <CreateClientModal
        isOpen={isCreateClient}
        onClose={() => setIsCreateClient(!isCreateClient)}
      />
      <CreateClientManagerModal
        isOpen={isCreateManager}
        onClose={() => setIsCreateManager(!isCreateManager)}
        clientId={Number(clientSelectionData.selectedClient)}
      />
      {currentServiceId && (
        <CustomModal visible={isTemplateModalOpen} setVisible={setIsTemplateModalOpen}>
          <CreateCommentTemplateModal
            onClose={handleCloseTemplateModal}
            onSubmit={handleTemplateSubmit}
            template={editingTemplate}
            serviceId={currentServiceId}
            serviceName={currentServiceName}
          />
        </CustomModal>
      )}
    </div>
  )
}

export default EditClaimCardMainInformation
